import React from "react";

/**
 * Glowing circular line-art overlays — the Lounge backdrop.
 *
 * Layered:
 *   1. Two large radial halos (silver + cyan) using `screen` blend
 *   2. Multiple concentric line-art rings, top-right + bottom-left
 *   3. A diagonal silver hairline running through the canvas
 *
 * Visibility was deliberately raised: the previous version was too subtle
 * after the dark-navy backdrop and high-DPI displays.
 */
const CircleGlow = ({ className = "", intensity = 1 }) => (
  <div
    aria-hidden
    className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
    style={{ isolation: "isolate" }}
  >
    {/* Silver halo, top-right */}
    <div
      className="absolute"
      style={{
        top: "-26%",
        right: "-18%",
        width: "880px",
        height: "880px",
        borderRadius: "9999px",
        background:
          "radial-gradient(closest-side, rgba(192,197,206,0.55), rgba(192,197,206,0) 70%)",
        filter: "blur(16px)",
        opacity: 0.9 * intensity,
        mixBlendMode: "screen",
      }}
    />
    {/* Cyan glow, bottom-left */}
    <div
      className="absolute"
      style={{
        bottom: "-30%",
        left: "-22%",
        width: "960px",
        height: "960px",
        borderRadius: "9999px",
        background:
          "radial-gradient(closest-side, rgba(91,192,235,0.45), rgba(91,192,235,0) 70%)",
        filter: "blur(20px)",
        opacity: 0.95 * intensity,
        mixBlendMode: "screen",
      }}
    />
    {/* Centre warm halo for depth */}
    <div
      className="absolute"
      style={{
        top: "20%",
        left: "30%",
        width: "640px",
        height: "640px",
        borderRadius: "9999px",
        background:
          "radial-gradient(closest-side, rgba(212,216,223,0.18), rgba(212,216,223,0) 70%)",
        filter: "blur(28px)",
        opacity: 0.85 * intensity,
        mixBlendMode: "screen",
      }}
    />

    {/* Concentric rings, top-right */}
    <svg
      className="absolute"
      style={{
        top: "-280px",
        right: "-280px",
        mixBlendMode: "screen",
        opacity: 1 * intensity,
      }}
      width="1000"
      height="1000"
      viewBox="0 0 1000 1000"
      fill="none"
    >
      {[480, 410, 340, 270, 200, 130, 70].map((r, i) => (
        <circle
          key={r}
          cx="500"
          cy="500"
          r={r}
          stroke="#C0C5CE"
          strokeWidth="1"
          opacity={0.16 + i * 0.025}
        />
      ))}
    </svg>

    {/* Concentric rings, bottom-left */}
    <svg
      className="absolute"
      style={{
        bottom: "-320px",
        left: "-320px",
        mixBlendMode: "screen",
        opacity: 1 * intensity,
      }}
      width="1100"
      height="1100"
      viewBox="0 0 1100 1100"
      fill="none"
    >
      {[540, 460, 380, 300, 220, 140].map((r, i) => (
        <circle
          key={r}
          cx="550"
          cy="550"
          r={r}
          stroke="#C0C5CE"
          strokeWidth="1"
          opacity={0.14 + i * 0.03}
        />
      ))}
    </svg>

    {/* Subtle diagonal hairline running across the canvas */}
    <div
      className="absolute"
      style={{
        top: "55%",
        left: "-10%",
        width: "120%",
        height: "1px",
        background:
          "linear-gradient(90deg, rgba(192,197,206,0) 0%, rgba(192,197,206,0.35) 45%, rgba(192,197,206,0.35) 55%, rgba(192,197,206,0) 100%)",
        transform: "rotate(-7deg)",
        mixBlendMode: "screen",
        opacity: 0.7 * intensity,
      }}
    />
  </div>
);

export default CircleGlow;
